/**
 * What to call a document on screen.
 *
 * `documents.filename` is a storage key, not a name. For anything imported from
 * Fasten it is an S3 key component that must never change — the backend rebuilds
 * the object path from it, and /documents/get-signed-url resolves through that
 * path — so the readable name lives in `documents.description`, and the ingest
 * realtime events carry it as `display_name` beside the key.
 *
 * Six call sites had this fallback open-coded in three different shapes, none
 * of them guarding the key itself: when `display_name` is absent (an event
 * published before the backend started sending it) they printed
 * `fasten_74adc38c-6ff5-11ef-bf2f-0a58a9feac02.txt`. This is the one definition.
 *
 * Port of the legacy web client's document-title utility — a document should
 * not be called one thing on web and another here.
 *
 * Keep using `doc.filename` directly for LOOKUPS (the get-signed-url request,
 * preview caches keyed by file) where the key is the point.
 */

// An importer owns this shape; no person names a file `fasten_<uuid>`.
// Mirrors the guard in backend/api/client/utilities/document-display-name.js.
const isStorageKey = (value: unknown): boolean =>
  /^fasten_[0-9a-f-]{8,}/i.test(String(value ?? '').trim());

const trimmed = (value: unknown): string => String(value ?? '').trim();

/**
 * @param doc       a documents row, or an ingest-event item carrying display_name
 * @param fallback  what to show when there is nothing readable
 */
export function documentDisplayName(doc: any, fallback = 'Untitled document'): string {
  // `display_name` first: on an ingest event it is the only readable name
  // present — the row's `description` isn't in that payload.
  const display = trimmed(doc?.display_name ?? doc?.displayName);
  if (display && !isStorageKey(display)) return display;

  const description = trimmed(doc?.description);
  if (description) return description;

  const filename = trimmed(doc?.filename);
  if (filename && !isStorageKey(filename)) return filename;

  return fallback;
}
